import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';
import { demoFunc } from './dees-dataview-codebox.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
  unsafeHTML,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import { syntaxPaletteStyles, syntaxScopeStyles } from '../../00syntax.js';

import type { HLJSApi } from 'highlight.js';

import * as smartstring from '@push.rocks/smartstring';

import { DeesContextmenu } from '../../00group-overlay/dees-contextmenu/dees-contextmenu.js';
import { DeesServiceLibLoader } from '../../../services/index.js';
import '../../00group-layout/dees-tile/dees-tile.js';
import '../../00group-utility/dees-icon/dees-icon.js';
import {
  buildSplitRows,
  computeLineDiff,
  diffStats,
  foldContextRows,
  parseUnifiedDiffDocument,
  toUnifiedDiff,
  type IDiffRow,
  type IDiffSegment,
  type ISplitCell,
  type TDiffDisplayRow,
  type IUnifiedDiffDocument,
  type IUnifiedDiffFileSection,
  type IUnifiedDiffMetadata,
} from './codebox.diff.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-dataview-codebox': DeesDataviewCodebox;
  }
}

export type TDiffView = 'inline' | 'split';
export type TDiffViewRequest = TDiffView | 'auto';

export interface IDiffViewChangeDetail {
  diffView: TDiffView;
}

@customElement('dees-dataview-codebox')
export class DeesDataviewCodebox extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Data View', 'Workspace'];

  @property()
  accessor progLang: string = 'typescript';

  /** Shown centered in the title bar; empty hides the title. */
  @property()
  accessor filename: string = '';

  /** Embed the viewer in a parent-owned frame. */
  @property({ type: Boolean, reflect: true })
  accessor frameless: boolean = false;

  @property({ type: String })
  accessor codeToDisplay: string = '';

  /**
   * Diff mode inputs: set `codeBefore` (compared against `codeToDisplay` as
   * the after state) OR a pre-computed `unifiedDiff` patch. Either activates
   * diff rendering; `diffView` picks the layout and can force diff mode on
   * its own (e.g. new-file diffs where `codeBefore` is empty).
   */
  @property({ type: String })
  accessor codeBefore: string = '';

  @property({ type: String })
  accessor unifiedDiff: string = '';

  /** The unified patch ends before the producer's complete output. */
  @property({ type: Boolean })
  accessor unifiedDiffTruncated: boolean = false;

  /** '' = regular code; 'inline' = unified rows; 'split' = side by side; 'auto' splits at 640px. */
  @property({ type: String })
  accessor diffView: '' | TDiffViewRequest = '';

  /** Hide gutters when a diff source does not provide trustworthy positions. */
  @property({ type: Boolean })
  accessor showDiffLineNumbers: boolean = true;

  @state()
  accessor highlightedCode: string = '';

  @state()
  accessor copied: boolean = false;

  @state()
  accessor expandedFolds: ReadonlySet<number> = new Set<number>();

  @state()
  accessor highlightVersion: number = 0;

  @state()
  private accessor autoDiffIsNarrow = true;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        position: relative;
        display: block;
        text-align: left;
        font-family: var(--dees-font-family);
        box-sizing: border-box;
        container-type: inline-size;
        container-name: dees-codebox;
      }

      dees-tile {
        height: 100%;
        color: var(--dees-color-text-primary);
      }

      /* --- Title bar --- */
      .appbar {
        min-height: var(--dees-control-height-sm);
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: center;
        padding: 0 var(--dees-spacing-sm) 0 var(--dees-spacing-lg);
      }

      .appbar .fileName {
        grid-column: 2;
        font-size: var(--dees-font-control-size-sm);
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .appbar .actions {
        grid-column: 3;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        justify-content: flex-end;
      }


      @container dees-codebox (width < 640px) {
        .appbar {
          grid-template-columns: minmax(0, 1fr) auto;
          gap: var(--dees-spacing-sm);
        }
        .appbar .fileName { grid-column: 1; min-width: 0; }
        .appbar .actions { grid-column: 2; }
      }


      .copyButton {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: var(--dees-radius-sm);
        color: var(--dees-color-text-muted);
        cursor: pointer;
        transition:
          background-color var(--dees-transition-fast) var(--dees-ease-standard),
          color var(--dees-transition-fast) var(--dees-ease-standard);
      }

      @media (pointer: coarse) { .copyButton { min-width: 44px; height: 44px; } }

      .copyButton:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-secondary);
      }

      .copyButton:active {
        background: var(--dees-color-active);
      }

      .copyButton.copied {
        color: var(--dees-color-text-success);
      }

      /* --- Code area --- */
      .codegrid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
      }

      .lineNumbers {
        padding: var(--dees-spacing-lg) var(--dees-spacing-md) var(--dees-spacing-lg) var(--dees-spacing-lg);
        text-align: right;
        color: var(--dees-color-text-muted);
        border-right: 1px solid var(--dees-color-border-subtle);
        user-select: none;
        min-width: 24px;
      }

      pre {
        margin: 0;
        padding: var(--dees-spacing-lg) var(--dees-spacing-xl);
        overflow-x: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      code {
        padding: 0;
        margin: 0;
        color: var(--dees-color-text-primary);
      }

      code,
      code *,
      .lineNumbers {
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-control-size-sm);
        font-weight: 400;
        line-height: 1.6;
      }

      /* --- Bottom bar --- */
      .bottomBar {
        height: 28px;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: 0 var(--dees-spacing-md) 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-caption2-size);
        color: var(--dees-color-text-muted);
        box-sizing: border-box;
      }

      .lineCount {
        margin-right: auto;
        font-weight: 500;
      }

      .languageLabel {
        font-size: var(--dees-font-caption2-size);
        font-weight: 500;
        color: var(--dees-color-accent-primary);
        background: color-mix(in srgb, var(--dees-color-accent-primary) 12%, transparent);
        padding: 2px 8px;
        border-radius: var(--dees-radius-full);
      }

    `,
    /* Shared system syntax palette + highlight.js scope mapping */
    syntaxPaletteStyles,
    syntaxScopeStyles,
    // --- Diff rendering ---
    css`
      :host {
        --codebox-diff-add-bg: color-mix(in srgb, var(--dees-color-accent-success) 14%, transparent);
        --codebox-diff-add-intra: color-mix(in srgb, var(--dees-color-accent-success) 34%, transparent);
        --codebox-diff-remove-bg: color-mix(in srgb, var(--dees-color-accent-error) 13%, transparent);
        --codebox-diff-remove-intra: color-mix(in srgb, var(--dees-color-accent-error) 32%, transparent);
      }

      .diffScroll {
        overflow-x: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
        padding: var(--dees-spacing-sm) 0;
      }

      .diffGrid {
        display: grid;
        min-width: max-content;
        width: 100%;
      }

      .diffGrid.inline {
        grid-template-columns: minmax(3ch, auto) minmax(3ch, auto) 2ch minmax(0, 1fr);
      }

      .diffGrid.inline.withoutLineNumbers {
        grid-template-columns: 2ch minmax(0, 1fr);
      }

      .diffSplit {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
        align-items: start;
        min-width: 0;
        padding: var(--dees-spacing-sm) 0;
      }

      .diffSplitDivider {
        align-self: stretch;
        background: var(--dees-color-border-default);
      }

      .diffSplitPane {
        min-width: 0;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
      }

      .diffGrid.splitSide {
        grid-template-columns: minmax(3ch, auto) 2ch minmax(24ch, 1fr);
      }

      .diffGrid.splitSide.withoutLineNumbers {
        grid-template-columns: 2ch minmax(24ch, 1fr);
      }

      .diffGrid > div {
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-control-size-sm);
        line-height: 1.6;
        white-space: pre;
      }

      .diffNum {
        padding: 0 var(--dees-spacing-sm);
        text-align: right;
        color: var(--dees-color-text-muted);
        user-select: none;
      }

      .diffSign {
        text-align: center;
        user-select: none;
        color: var(--dees-color-text-muted);
      }

      .diffText {
        padding-right: var(--dees-spacing-lg);
      }

      .diffGrid .row-add {
        background: var(--codebox-diff-add-bg);
      }

      .diffGrid .row-add.diffSign {
        color: var(--dees-color-accent-success);
        font-weight: 600;
      }

      .diffGrid .row-remove {
        background: var(--codebox-diff-remove-bg);
      }

      .diffGrid .row-remove.diffSign {
        color: var(--dees-color-accent-error);
        font-weight: 600;
      }

      .diffGrid .row-filler {
        background: color-mix(in srgb, var(--dees-color-text-muted) 5%, transparent);
      }

      .intra-add {
        background: var(--codebox-diff-add-intra);
        border-radius: 2px;
      }

      .intra-remove {
        background: var(--codebox-diff-remove-intra);
        border-radius: 2px;
      }

      .diffHunk {
        grid-column: 1 / -1;
        padding: 2px var(--dees-spacing-lg);
        background: var(--dees-color-bg-tertiary);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption2-size);
        user-select: none;
      }

      .diffFold {
        grid-column: 1 / -1;
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        padding: 2px var(--dees-spacing-lg);
        border: 0;
        background: color-mix(in srgb, var(--dees-color-accent-primary) 7%, transparent);
        color: var(--dees-color-link);
        cursor: pointer;
        font-family: var(--dees-font-family-mono);
        font-size: var(--dees-font-caption1-size, 11px);
        text-align: left;
      }

      .diffFold:hover {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 13%, transparent);
        color: var(--dees-color-link-hover);
      }

      .diffStat {
        font-weight: 600;
      }

      .diffStat.added {
        color: var(--dees-color-accent-success);
      }

      .diffStat.removed {
        color: var(--dees-color-accent-error);
      }

      .diffFileHeader {
        display: flex;
        align-items: baseline;
        gap: var(--dees-spacing-md);
        min-width: 0;
        padding: 6px var(--dees-spacing-lg);
        border-top: 1px solid var(--dees-color-border-subtle);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        background: var(--dees-color-bg-secondary);
      }

      .diffFile:first-child > .diffFileHeader { border-top: 0; }

      .diffFilePath {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--dees-color-text-primary);
        font: 600 var(--dees-font-control-size-sm)/1.4 var(--dees-font-family-mono);
      }

      .diffFileMeta {
        flex: 0 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .diffFileNotice,
      .diffDocumentNotice {
        padding: var(--dees-spacing-lg);
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-control-size-sm);
      }

      .diffQualifier {
        margin-left: var(--dees-spacing-sm);
        color: var(--dees-color-text-muted);
        font-weight: 500;
      }
    `,
  ];

  render(): TemplateResult {
    const contextMenuOptions = [
      ...(!this.unifiedDiff
        ? [{
            name: 'Copy Code',
            iconName: 'lucide:Copy',
            action: async () => {
              await this.copyCode();
            },
          }]
        : []),
      ...(this.isDiffActive
        ? [{
            name: 'Copy Unified Diff',
            iconName: 'lucide:GitCompareArrows',
            action: async () => {
              await this.copyUnifiedDiff();
            },
          }]
        : []),
    ];
    return html`
      <dees-tile
        .frameless=${this.frameless}
        @contextmenu="${(eventArg: MouseEvent) => {
          DeesContextmenu.openContextMenuWithOptions(eventArg, contextMenuOptions);
        }}"
      >
        <div slot="header" class="appbar">
          <div class="fileName">${this.filename}</div>
          <div class="actions">
            ${this.isDiffActive
              ? html`
                  <dees-input-multitoggle size="sm" label="Diff layout" .labelPosition=${'none'}
                    .options=${[{key:'inline',label:'Inline'},{key:'split',label:'Split'}]} .selectedOption=${this.effectiveDiffView}
                    @input=${(event: Event) => event.stopPropagation()}
                    @change=${(event: Event) => event.stopPropagation()}
                    @option-activate=${(event: CustomEvent) => { event.stopPropagation(); this.setDiffView(event.detail.value); }}></dees-input-multitoggle>
                `
              : ''}
            ${!this.unifiedDiff ? html`
              <div
                class="copyButton ${this.copied ? 'copied' : ''}"
                title="Copy code"
                @click=${this.copyCode}
              >
                <dees-icon .icon=${this.copied ? 'lucide:Check' : 'lucide:Copy'} iconSize="14"></dees-icon>
              </div>
            ` : ''}
          </div>
        </div>
        ${this.isDiffActive ? this.renderDiff() : this.renderCode()}
        <div slot="footer" class="bottomBar">
          ${this.isDiffActive ? this.renderDiffFooter() : this.renderCodeFooter()}
          <div class="languageLabel">${this.progLang}</div>
        </div>
      </dees-tile>
    `;
  }

  private renderCode(): TemplateResult {
    const codeLines = this.processedCode ? this.processedCode.split('\n') : [];
    return html`
      <div class="codegrid">
        <div class="lineNumbers">
          ${codeLines.map((_, index) => html`<div class="lineCounter">${index + 1}</div>`)}
        </div>
        <pre><code>${this.highlightedCode
          ? unsafeHTML(this.highlightedCode)
          : this.processedCode}</code></pre>
      </div>
    `;
  }

  private renderCodeFooter(): TemplateResult {
    const codeLines = this.processedCode ? this.processedCode.split('\n') : [];
    return html`<div class="lineCount">${codeLines.length} ${codeLines.length === 1 ? 'line' : 'lines'}</div>`;
  }

  private renderDiffFooter(): TemplateResult {
    const summary = this.diffDocument?.summary;
    const stats = summary
      ? summary.type === 'unparseable' ? undefined : summary.stats
      : diffStats(this.diffRows);
    const qualifiers: string[] = [];
    if (summary?.type === 'partial') qualifiers.push('Partial diff');
    if (summary?.type === 'unparseable') qualifiers.push('Diff format unavailable');
    if (summary?.binaryFiles) {
      qualifiers.push(`${summary.binaryFiles} binary ${summary.binaryFiles === 1 ? 'file' : 'files'}`);
    }
    if (summary?.type === 'exact' && !stats && !summary.binaryFiles) qualifiers.push('Metadata-only diff');
    return html`
      <div class="lineCount">
        ${stats ? html`
          <span class="diffStat added">+${stats.added}</span>
          <span class="diffStat removed">−${stats.removed}</span>
        ` : ''}
        ${qualifiers.length ? html`<span class="diffQualifier">${qualifiers.join(' · ')}</span>` : ''}
      </div>
    `;
  }

  private renderDiff(): TemplateResult {
    const sections = this.displaySectionsWithFolds();
    if (!sections.length) {
      return html`<div class="diffDocumentNotice">The diff has no displayable file sections.</div>`;
    }
    return this.effectiveDiffView === 'split'
      ? html`${sections.map(({ file, rows }) => html`
          <section class="diffFile">
            ${this.renderDiffFileHeader(file)}
            ${rows.length ? this.renderSplitGrid(rows) : this.renderDiffFileNotice(file)}
          </section>
        `)}`
      : html`<div class="diffScroll">${sections.map(({ file, rows }) => html`
          <section class="diffFile">
            ${this.renderDiffFileHeader(file)}
            ${rows.length ? this.renderInlineGrid(rows) : this.renderDiffFileNotice(file)}
          </section>
        `)}</div>`;
  }

  private renderDiffFileHeader(fileArg: IUnifiedDiffFileSection): TemplateResult | '' {
    const details = this.diffFileMetadataLabels(fileArg);
    const path = fileArg.beforePath && fileArg.afterPath && fileArg.beforePath !== fileArg.afterPath
      ? `${fileArg.beforePath} → ${fileArg.afterPath}`
      : fileArg.displayPath;
    if (!path && !details.length) return '';
    return html`
      <div class="diffFileHeader">
        <div class="diffFilePath">${path ?? 'Unscoped patch'}</div>
        ${details.length ? html`<div class="diffFileMeta">${details.join(' · ')}</div>` : ''}
      </div>
    `;
  }

  private diffFileMetadataLabels(fileArg: IUnifiedDiffFileSection): string[] {
    const labels: string[] = [];
    const metadata = (kindArg: IUnifiedDiffMetadata['kind']) =>
      fileArg.metadata.filter((entry) => entry.kind === kindArg).map((entry) => entry.value);
    const newFileMode = metadata('new-file-mode')[0];
    const deletedFileMode = metadata('deleted-file-mode')[0];
    const oldMode = metadata('old-mode')[0];
    const newMode = metadata('new-mode')[0];
    if (newFileMode) labels.push(`New file · mode ${newFileMode}`);
    if (deletedFileMode) labels.push(`Deleted file · mode ${deletedFileMode}`);
    if (metadata('rename-from').length || metadata('rename-to').length) labels.push('Renamed');
    if (metadata('copy-from').length || metadata('copy-to').length) labels.push('Copied');
    if (oldMode || newMode) labels.push(`Mode ${oldMode ?? '?'} → ${newMode ?? '?'}`);
    labels.push(...metadata('similarity-index').map((value) => `${value} similar`));
    labels.push(...metadata('dissimilarity-index').map((value) => `${value} dissimilar`));
    if (fileArg.binary) labels.push('Binary change');
    return labels;
  }

  private renderDiffFileNotice(fileArg: IUnifiedDiffFileSection): TemplateResult {
    return html`<div class="diffFileNotice">${fileArg.binary
      ? 'Binary content is not shown.'
      : 'No textual line changes.'}</div>`;
  }

  private renderInlineGrid(displayRows: TDiffDisplayRow[]): TemplateResult {
    return html`
      <div class="diffGrid inline ${this.showDiffLineNumbers ? '' : 'withoutLineNumbers'}">
        ${displayRows.map((displayRow) => {
          if (displayRow.kind === 'fold') {
            return this.renderFoldRow(displayRow.rows.length, displayRow.ordinal ?? 0);
          }
          if (displayRow.kind === 'hunk') {
            return html`<div class="diffHunk">${displayRow.text}</div>`;
          }
          const rowClass = displayRow.kind === 'add' ? 'row-add' : displayRow.kind === 'remove' ? 'row-remove' : '';
          return html`
            ${this.showDiffLineNumbers
              ? html`
                  <div class="diffNum ${rowClass}">${displayRow.oldLine ?? ''}</div>
                  <div class="diffNum ${rowClass}">${displayRow.newLine ?? ''}</div>
                `
              : ''}
            <div class="diffSign ${rowClass}">${displayRow.kind === 'add' ? '+' : displayRow.kind === 'remove' ? '−' : ''}</div>
            <div class="diffText ${rowClass}">${this.renderDiffLine(displayRow.text, displayRow.segments, displayRow.kind)}</div>
          `;
        })}
      </div>
    `;
  }

  private renderSplitGrid(displayRows: TDiffDisplayRow[]): TemplateResult {
    const leftRendered: TemplateResult[] = [];
    const rightRendered: TemplateResult[] = [];
    let run: IDiffRow[] = [];
    const flushRun = () => {
      for (const splitRow of buildSplitRows(run)) {
        if (splitRow.kind === 'hunk') {
          leftRendered.push(html`<div class="diffHunk">${splitRow.text}</div>`);
          rightRendered.push(html`<div class="diffHunk">${splitRow.text}</div>`);
        } else {
          leftRendered.push(this.renderSplitCell(splitRow.left));
          rightRendered.push(this.renderSplitCell(splitRow.right));
        }
      }
      run = [];
    };
    for (const displayRow of displayRows) {
      if (displayRow.kind === 'fold') {
        flushRun();
        leftRendered.push(this.renderFoldRow(displayRow.rows.length, displayRow.ordinal ?? 0));
        rightRendered.push(this.renderFoldRow(displayRow.rows.length, displayRow.ordinal ?? 0));
      } else {
        run.push(displayRow);
      }
    }
    flushRun();
    const lineNumberClass = this.showDiffLineNumbers ? '' : 'withoutLineNumbers';
    return html`
      <div class="diffSplit">
        <div class="diffSplitPane" data-side="left" aria-label="Before">
          <div class="diffGrid splitSide ${lineNumberClass}">${leftRendered}</div>
        </div>
        <div class="diffSplitDivider" aria-hidden="true"></div>
        <div class="diffSplitPane" data-side="right" aria-label="After">
          <div class="diffGrid splitSide ${lineNumberClass}">${rightRendered}</div>
        </div>
      </div>
    `;
  }

  private renderSplitCell(cell: ISplitCell | undefined): TemplateResult {
    if (!cell) {
      return html`
        ${this.showDiffLineNumbers ? html`<div class="diffNum row-filler"></div>` : ''}
        <div class="diffSign row-filler"></div>
        <div class="diffText row-filler"></div>
      `;
    }
    const rowClass = cell.kind === 'add' ? 'row-add' : cell.kind === 'remove' ? 'row-remove' : '';
    return html`
      ${this.showDiffLineNumbers ? html`<div class="diffNum ${rowClass}">${cell.line || ''}</div>` : ''}
      <div class="diffSign ${rowClass}">${cell.kind === 'add' ? '+' : cell.kind === 'remove' ? '−' : ''}</div>
      <div class="diffText ${rowClass}">${this.renderDiffLine(cell.text, cell.segments, cell.kind)}</div>
    `;
  }

  private renderFoldRow(hiddenCount: number, foldOrdinal: number): TemplateResult {
    return html`
      <button
        class="diffFold"
        type="button"
        @click=${() => {
          const expanded = new Set(this.expandedFolds);
          expanded.add(foldOrdinal);
          this.expandedFolds = expanded;
        }}
      >
        <dees-icon icon="lucide:UnfoldVertical" iconSize="12"></dees-icon>
        ${hiddenCount} unchanged ${hiddenCount === 1 ? 'line' : 'lines'}
      </button>
    `;
  }

  private renderDiffLine(
    text: string,
    segments: IDiffSegment[] | undefined,
    kind: 'context' | 'add' | 'remove',
  ): TemplateResult {
    if (segments?.length) {
      const intraClass = kind === 'add' ? 'intra-add' : 'intra-remove';
      return html`${segments.map((segment) =>
        segment.changed ? html`<span class=${intraClass}>${segment.text}</span>` : segment.text,
      )}`;
    }
    const highlighted = this.diffHighlightCache.get(text);
    return highlighted ? html`${unsafeHTML(highlighted)}` : html`${text || ' '}`;
  }

  private processedCode = '';
  private highlightJs: HLJSApi | null = null;
  private highlightToken = 0;
  private copiedResetTimeout: ReturnType<typeof setTimeout> | null = null;
  private diffRows: IDiffRow[] = [];
  private diffDocument: IUnifiedDiffDocument | null = null;
  private diffHighlightCache = new Map<string, string>();
  private autoDiffResizeObserver: ResizeObserver | null = null;

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    this.syncAutoDiffObservation();
  }

  private syncAutoDiffObservation(): void {
    if (!this.isConnected || this.diffView !== 'auto') {
      this.autoDiffResizeObserver?.disconnect();
      this.autoDiffResizeObserver = null;
      return;
    }
    if (this.autoDiffResizeObserver) return;
    const observer = new ResizeObserver((entries) => {
      if (this.autoDiffResizeObserver !== observer || !this.isConnected || this.diffView !== 'auto') return;
      const entry = entries.find((candidate) => candidate.target === this);
      if (!entry) return;
      const narrow = (entry.contentBoxSize[0]?.inlineSize ?? entry.contentRect.width) < 640;
      // Pixel-by-pixel resizing does not invalidate rendering or highlight caches.
      if (narrow !== this.autoDiffIsNarrow) this.autoDiffIsNarrow = narrow;
    });
    this.autoDiffResizeObserver = observer;
    observer.observe(this);
  }

  /** Diff mode is active when a diff input or an explicit diffView is set. */
  public get isDiffActive(): boolean {
    return Boolean(this.unifiedDiff || this.codeBefore || this.diffView);
  }

  public get effectiveDiffView(): TDiffView {
    return this.diffView === 'split' || (this.diffView === 'auto' && !this.autoDiffIsNarrow)
      ? 'split' : 'inline';
  }

  private setDiffView(diffViewArg: TDiffView): void {
    if (this.diffView !== 'auto' && this.effectiveDiffView === diffViewArg) return;
    this.diffView = diffViewArg;
    this.dispatchEvent(new CustomEvent<IDiffViewChangeDetail>('diff-view-change', {
      detail: { diffView: diffViewArg },
      bubbles: true,
      composed: true,
    }));
  }

  public willUpdate(changedProperties: Map<string, any>) {
    super.willUpdate(changedProperties);
    if (changedProperties.has('codeToDisplay')) {
      this.processedCode = this.codeToDisplay
        ? smartstring.indent.normalize(this.codeToDisplay).trim()
        : '';
      // drop the stale highlight so the new code renders plain until re-highlighted
      this.highlightedCode = '';
    }
    if (
      changedProperties.has('codeToDisplay')
      || changedProperties.has('codeBefore')
      || changedProperties.has('unifiedDiff')
      || changedProperties.has('unifiedDiffTruncated')
      || changedProperties.has('diffView')
    ) {
      if (this.isDiffActive) {
        if (this.unifiedDiff) {
          this.diffDocument = parseUnifiedDiffDocument(this.unifiedDiff, {
            truncated: this.unifiedDiffTruncated,
          });
          this.diffRows = this.diffDocument.files.flatMap((file) => file.rows);
        } else {
          this.diffDocument = null;
          this.diffRows = computeLineDiff(this.codeBefore, this.codeToDisplay);
        }
      } else {
        this.diffDocument = null;
        this.diffRows = [];
      }
      this.diffHighlightCache.clear();
      this.expandedFolds = new Set<number>();
    }
    // language switch invalidates cached per-line highlights
    if (changedProperties.has('progLang')) {
      this.diffHighlightCache.clear();
    }
  }

  private displaySectionsWithFolds(): Array<{
    file: IUnifiedDiffFileSection;
    rows: TDiffDisplayRow[];
  }> {
    const files = this.diffDocument?.files ?? [{
      metadata: [],
      rows: this.diffRows,
      binary: false,
    } satisfies IUnifiedDiffFileSection];
    let foldOrdinal = 0;
    return files.map((file) => {
      const result: TDiffDisplayRow[] = [];
      for (const row of foldContextRows(file.rows)) {
        if (row.kind === 'fold') {
          if (this.expandedFolds.has(foldOrdinal)) {
            result.push(...row.rows);
          } else {
            result.push({ ...row, ordinal: foldOrdinal });
          }
          foldOrdinal++;
        } else {
          result.push(row);
        }
      }
      return { file, rows: result };
    });
  }

  private displayRowsWithFolds(): TDiffDisplayRow[] {
    return this.displaySectionsWithFolds().flatMap((section) => section.rows);
  }

  public async updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('diffView')) this.syncAutoDiffObservation();
    // adopt slotted text content as code source once (regular mode only)
    if (!this.codeToDisplay && !this.isDiffActive) {
      const slottedText = Array.from(this.childNodes).find(
        (childNode) => childNode.nodeName === '#text' && (childNode as Text).wholeText.trim(),
      ) as Text | undefined;
      if (slottedText) {
        this.codeToDisplay = slottedText.wholeText;
        return;
      }
    }
    if (this.isDiffActive) {
      if (
        changedProperties.has('codeToDisplay')
        || changedProperties.has('codeBefore')
        || changedProperties.has('unifiedDiff')
        || changedProperties.has('unifiedDiffTruncated')
        || changedProperties.has('diffView')
        || changedProperties.has('expandedFolds')
        || changedProperties.has('progLang')
      ) {
        await this.highlightDiffLines();
      }
      return;
    }
    if (changedProperties.has('codeToDisplay') || changedProperties.has('progLang')) {
      await this.highlightCode();
    }
  }

  /**
   * Highlights visible diff lines per-line (full-text highlighting cannot be
   * split across rows because hljs spans may cross line boundaries). Results
   * are cached by line text; intraline-segmented rows skip highlighting —
   * their change tint carries the information.
   */
  private async highlightDiffLines() {
    const token = ++this.highlightToken;
    if (!this.highlightJs) {
      this.highlightJs = await DeesServiceLibLoader.getInstance().loadHighlightJs();
    }
    if (token !== this.highlightToken) return;
    const pendingLines = new Set<string>();
    for (const displayRow of this.displayRowsWithFolds()) {
      if (displayRow.kind === 'fold' || displayRow.kind === 'hunk') continue;
      if (displayRow.segments?.length) continue;
      if (displayRow.text && !this.diffHighlightCache.has(displayRow.text)) {
        pendingLines.add(displayRow.text);
      }
    }
    if (!pendingLines.size) return;
    for (const line of pendingLines) {
      try {
        this.diffHighlightCache.set(
          line,
          this.highlightJs.highlight(line, { language: this.progLang, ignoreIllegals: true }).value,
        );
      } catch {
        this.diffHighlightCache.set(line, '');
      }
    }
    if (token === this.highlightToken) {
      this.highlightVersion++;
    }
  }

  private async highlightCode() {
    if (!this.processedCode) {
      return;
    }
    const token = ++this.highlightToken;
    if (!this.highlightJs) {
      this.highlightJs = await DeesServiceLibLoader.getInstance().loadHighlightJs();
    }
    if (token !== this.highlightToken) {
      return;
    }
    try {
      this.highlightedCode = this.highlightJs.highlight(this.processedCode, {
        language: this.progLang,
        ignoreIllegals: true,
      }).value;
    } catch (error) {
      // unknown language — keep the plain-text rendering
      console.warn(`dees-dataview-codebox: could not highlight as '${this.progLang}'`, error);
    }
  }

  private copyCode = async () => {
    if (this.unifiedDiff) {
      return;
    }
    // in diff mode the copy button yields the after-state text
    const textToCopy = this.isDiffActive
      ? this.diffRows
          .filter((row) => row.kind === 'context' || row.kind === 'add')
          .map((row) => row.text)
          .join('\n')
      : this.processedCode;
    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (error) {
      console.error('dees-dataview-codebox: copy failed', error);
      return;
    }
    this.copied = true;
    if (this.copiedResetTimeout) {
      clearTimeout(this.copiedResetTimeout);
    }
    this.copiedResetTimeout = setTimeout(() => {
      this.copied = false;
      this.copiedResetTimeout = null;
    }, 1500);
  };

  private copyUnifiedDiff = async () => {
    const patch = this.unifiedDiff || toUnifiedDiff(this.diffRows, {
      fileNameBefore: this.filename ? `a/${this.filename}` : undefined,
      fileNameAfter: this.filename ? `b/${this.filename}` : undefined,
    });
    try {
      await navigator.clipboard.writeText(patch);
    } catch (error) {
      console.error('dees-dataview-codebox: copy unified diff failed', error);
    }
  };

  public async disconnectedCallback() {
    this.autoDiffResizeObserver?.disconnect();
    this.autoDiffResizeObserver = null;
    if (this.copiedResetTimeout) {
      clearTimeout(this.copiedResetTimeout);
      this.copiedResetTimeout = null;
    }
    await super.disconnectedCallback();
  }
}
